/**
 * MapPicker – Carte interactive pour choisir un point (lat/lng).
 * Clique sur la carte ou saisie manuelle. Sans OSM, adapté mobile.
 */

import { useCallback, useEffect, useId, useMemo, useRef } from 'react'
import {
  Layer,
  Marker,
  Source,
  useMap,
  type MapLayerMouseEvent,
} from '@vis.gl/react-maplibre'
import type { FeatureCollection, MultiPolygon, Polygon } from 'geojson'
import { BaseMap } from '../../map/components/BaseMap'
import type { MapPickerExistingPoint } from './mapPickerPoints'
import './MapPicker.css'
import { useTranslation } from 'react-i18next'

export interface MapPickerPoint {
  lat: number
  lng: number
}

export interface MapPickerBounds {
  west: number
  south: number
  east: number
  north: number
}

export type MapPickerBoundaryGeometry = Polygon | MultiPolygon

interface MapPickerProps {
  /** Point actuel (undefined = non défini) */
  value: MapPickerPoint | null | undefined
  /** Appelé quand l'utilisateur change le point (carte ou champs) */
  onChange: (point: MapPickerPoint | null) => void
  /** Centre par défaut si aucun point */
  defaultCenter?: [number, number]
  /** Zoom initial (ignoré si fitBounds fourni) */
  defaultZoom?: number
  /** Emprise à cadrer au chargement (ex. boundary du parc) */
  fitBounds?: MapPickerBounds | null
  /** Périmètre à dessiner (Polygon / MultiPolygon GeoJSON) */
  boundaryGeometry?: MapPickerBoundaryGeometry | null
  /** Hauteur du bloc carte (CSS) */
  height?: string
  /** Désactiver carte et champs */
  disabled?: boolean
  /** Afficher les champs lat/lng (défaut: true) */
  showCoordinateFields?: boolean
  /** Texte d’aide sous les champs / au-dessus de la carte */
  hint?: string
  /** Id du label pour accessibilité */
  labelId?: string
  /** Points déjà enregistrés (ex. les autres emplacements), affichés pour repère */
  existingPoints?: MapPickerExistingPoint[]
}

function FitBoundsOnLoad({ bounds }: { bounds: MapPickerBounds }) {
  const { current: map } = useMap()
  useEffect(() => {
    if (!map) return
    const apply = () => {
      map.fitBounds(
        [
          [bounds.west, bounds.south],
          [bounds.east, bounds.north],
        ],
        { padding: 48, maxZoom: 16, duration: 0 },
      )
    }
    if (map.isStyleLoaded()) apply()
    else map.once('load', apply)
  }, [map, bounds.west, bounds.south, bounds.east, bounds.north])
  return null
}

/** Cadre la caméra sur le point au premier affichage (pas à chaque clic). */
function JumpToPointOnLoad({
  value,
  zoom,
  enabled,
}: {
  value: MapPickerPoint | null | undefined
  zoom: number
  enabled: boolean
}) {
  const { current: map } = useMap()
  const didJump = useRef(false)

  useEffect(() => {
    if (!value) {
      didJump.current = false
      return
    }
    if (!enabled || !map || didJump.current) return
    if (!Number.isFinite(value.lat) || !Number.isFinite(value.lng)) return
    didJump.current = true
    const apply = () => {
      map.jumpTo({ center: [value.lng, value.lat], zoom })
    }
    if (map.isStyleLoaded()) apply()
    else map.once('load', apply)
  }, [enabled, map, value, zoom])

  return null
}

function BoundaryOverlay({
  geometry,
  sourceId,
}: {
  geometry: MapPickerBoundaryGeometry
  sourceId: string
}) {
  const data = useMemo<FeatureCollection<Polygon | MultiPolygon>>(
    () => ({
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: {},
          geometry,
        },
      ],
    }),
    [geometry],
  )

  return (
    <Source id={sourceId} type="geojson" data={data}>
      <Layer
        id={`${sourceId}-fill`}
        type="fill"
        paint={{
          'fill-color': '#16a34a',
          'fill-opacity': 0.12,
        }}
      />
      <Layer
        id={`${sourceId}-line`}
        type="line"
        layout={{ 'line-join': 'round', 'line-cap': 'round' }}
        paint={{
          'line-color': '#15803d',
          'line-width': 2.5,
          'line-opacity': 0.8,
        }}
      />
    </Source>
  )
}

const DEFAULT_POINT_ZOOM = 14

export function MapPicker({
  value,
  onChange,
  defaultCenter = [46.3432, -72.5793],
  defaultZoom = 5,
  fitBounds = null,
  boundaryGeometry = null,
  height = '220px',
  disabled = false,
  showCoordinateFields = true,
  hint: hintProp,
  labelId,
  existingPoints = [],
}: MapPickerProps) {
  const { t } = useTranslation('organizations')
  const hint = hintProp ?? t('MapPicker.aide_par_defaut')
  const boundarySourceId = useId().replace(/:/g, '')
  const initialCenter: [number, number] = value
    ? [value.lat, value.lng]
    : defaultCenter

  const handleMapClick = useCallback(
    (e: MapLayerMouseEvent) => {
      if (disabled) return
      const { lat, lng } = e.lngLat
      if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
        onChange({ lat, lng })
      }
    },
    [onChange, disabled],
  )

  const handleLatChange = useCallback(
    (v: string) => {
      const n = parseFloat(v)
      if (v === '' || v === '-') {
        onChange(value ? { ...value, lat: 0 } : null)
        return
      }
      if (!Number.isNaN(n)) {
        onChange(value ? { ...value, lat: n } : { lat: n, lng: 0 })
      }
    },
    [value, onChange],
  )

  const handleLngChange = useCallback(
    (v: string) => {
      const n = parseFloat(v)
      if (v === '' || v === '-') {
        onChange(value ? { ...value, lng: 0 } : null)
        return
      }
      if (!Number.isNaN(n)) {
        onChange(value ? { ...value, lng: n } : { lat: 0, lng: n })
      }
    },
    [value, onChange],
  )

  const mapContainerStyle = useMemo(
    () => ({ height, width: '100%' }) as React.CSSProperties,
    [height],
  )

  const pointZoom = Math.max(defaultZoom, DEFAULT_POINT_ZOOM)
  const initialZoom = fitBounds
    ? Math.min(defaultZoom, 12)
    : value
      ? pointZoom
      : defaultZoom

  return (
    <div className={`map-picker${showCoordinateFields ? '' : ' map-picker--map-only'}`}>
      {labelId && (
        <span id={labelId} className="map-picker__label-ref">
          {t('MapPicker.coordonnees')}
        </span>
      )}
      {showCoordinateFields && (
        <>
          <div className="map-picker__coordinates">
            <div className="map-picker__coord">
              <label htmlFor="map-picker-lat" className="map-picker__coord-label">
                {t('MapPicker.latitude')}
              </label>
              <input
                id="map-picker-lat"
                type="number"
                min={-90}
                max={90}
                step="any"
                value={value?.lat ?? ''}
                onChange={(e) => handleLatChange(e.target.value)}
                disabled={disabled}
                placeholder={t('MapPicker.ex_46_2')}
                className="map-picker__input"
                aria-label={t('MapPicker.latitude')}
              />
            </div>
            <div className="map-picker__coord">
              <label htmlFor="map-picker-lng" className="map-picker__coord-label">
                {t('MapPicker.longitude')}
              </label>
              <input
                id="map-picker-lng"
                type="number"
                min={-180}
                max={180}
                step="any"
                value={value?.lng ?? ''}
                onChange={(e) => handleLngChange(e.target.value)}
                disabled={disabled}
                placeholder={t('MapPicker.ex_72_5')}
                className="map-picker__input"
                aria-label={t('MapPicker.longitude')}
              />
            </div>
          </div>
          {hint ? <p className="map-picker__hint">{hint}</p> : null}
        </>
      )}
      {!showCoordinateFields && hint ? (
        <p className="map-picker__hint">{hint}</p>
      ) : null}
      <div className="map-picker__map" style={mapContainerStyle}>
        <BaseMap
          initialViewState={{
            longitude: initialCenter[1],
            latitude: initialCenter[0],
            zoom: initialZoom,
          }}
          style={{ width: '100%', height: '100%' }}
          onClick={handleMapClick}
        >
          {fitBounds && <FitBoundsOnLoad bounds={fitBounds} />}
          {!fitBounds && (
            <JumpToPointOnLoad value={value} zoom={pointZoom} enabled={!!value} />
          )}
          {boundaryGeometry && (
            <BoundaryOverlay
              geometry={boundaryGeometry}
              sourceId={`map-picker-boundary-${boundarySourceId}`}
            />
          )}
          {existingPoints.map((p) => (
            <Marker key={`existing-${p.id}`} longitude={p.lng} latitude={p.lat} anchor="center">
              <span className="map-picker__existing" title={p.label ?? undefined}>
                <span className="map-picker__existing-dot" aria-hidden />
                {p.label && <span className="map-picker__existing-label">{p.label}</span>}
              </span>
            </Marker>
          ))}
          {value && (
            <Marker longitude={value.lng} latitude={value.lat} anchor="bottom" />
          )}
        </BaseMap>
      </div>
    </div>
  )
}
